import React from 'react';
import {
  ArrowRightIcon,
  ServerStackIcon,
  SignalIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import {
  CurrencyDollarIcon as cd,
  BanknotesIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';

function FintechHero() {
  return (
    <section className="relative overflow-hidden px-6 pb-32 pt-40">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(252,211,77,0.18),transparent_60%)]" />
        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(252,211,77,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(252,211,77,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <div className="mx-auto max-w-6xl text-left text-gray-800">
        {/* Caption */}
        <span className="animate-fadeUp mb-4 block text-sm font-medium tracking-wide text-orange-500">
          Scalable Fintech Solutions
        </span>

        <h1 className="mb-8 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
          <span className="animate-fadeUp block dark:text-gray-100">Financial Innovation</span>
          <span className="animate-fadeUp animate-gradientFlow mt-2 block bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent delay-150">
            Built for Real Impact
          </span>
        </h1>

        <p className="animate-fadeUp max-w-2xl text-base leading-relaxed delay-300 sm:text-lg dark:text-gray-100/90">
          We design and deploy fintech systems that handle real transactions, integrate securely
          with banking infrastructure, and scale effortlessly to serve millions of users.
        </p>

        {/* Key Highlights */}
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:gap-8">
          <div className="flex items-center gap-3 rounded-full bg-white/10 px-4 py-2 shadow-md backdrop-blur-lg">
            <ServerStackIcon className="h-6 w-6 text-yellow-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Robust Architecture
            </span>
          </div>
          <div className="flex items-center gap-3 rounded-full bg-white/10 px-4 py-2 shadow-md backdrop-blur-lg">
            <CurrencyDollarIcon className="h-6 w-6 text-orange-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Fast & Secure Payments
            </span>
          </div>
          <div className="flex items-center gap-3 rounded-full bg-white/10 px-4 py-2 shadow-md backdrop-blur-lg">
            <SignalIcon className="h-6 w-6 text-red-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Seamless Integrations
            </span>
          </div>
        </div>
      </div>

      {/* Key Highlights */}
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      icon: cd,
      title: 'Secure Payments',
      description:
        'All transactions are encrypted end-to-end, ensuring fast, safe, and reliable payment processing for your business and customers.',
      color: 'from-yellow-400 to-orange-400',
    },
    {
      icon: BanknotesIcon,
      title: 'Smart Investment Tools',
      description:
        'Automated investment algorithms help optimize your portfolio, reduce risk, and maximize returns seamlessly.',
      color: 'from-orange-400 to-red-400',
    },
    {
      icon: ArrowPathIcon,
      title: 'Seamless Integrations',
      description:
        'Connect effortlessly with banking APIs, fintech platforms, and accounting systems to streamline your financial operations.',
      color: 'from-red-400 to-pink-500',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Regulatory Compliance',
      description:
        'We ensure all systems meet financial regulations, KYC/AML standards, and industry best practices.',
      color: 'from-pink-500 to-purple-500',
    },
  ];
  return (
    <section className="relative mx-auto max-w-6xl overflow-hidden px-6 py-24">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* Multiple layered gradients for depth */}
        {/* Animated grid */}
        {/* <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(251,191,36,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(251,191,36,0.1)_1px,transparent_1px)] bg-[size:80px_80px] animate-pulse" /> */}
      </div>

      <div className="relative z-10 mx-auto max-w-6xl text-center">
        <span className="mb-4 inline-block rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold tracking-wide text-amber-600 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
          How It Works
        </span>

        <h2 className="mb-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl dark:text-white">
          Modern Fintech
          <span className="mt-2 block bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 bg-clip-text text-transparent">
            Innovation Pipeline
          </span>
        </h2>

        <p className="mx-auto mb-16 max-w-3xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
          Discover how cutting-edge fintech solutions revolutionize financial operations through
          secure processing, intelligent automation, and seamless integrations that scale with your
          business needs.
        </p>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative flex flex-col items-center gap-6 rounded-2xl border border-gray-200/50 bg-white/95 p-8 shadow-xl backdrop-blur-xl transition-all duration-500 hover:scale-[1.02] hover:bg-white hover:shadow-2xl dark:border-gray-700/50 dark:bg-gray-800/95 dark:hover:bg-gray-800"
              >
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-amber-500/5 via-orange-500/5 to-red-500/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                {/* Icon container */}
                <div className="relative">
                  <div
                    className={`relative flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br ${step.color} text-white shadow-lg transition-all duration-500 group-hover:rotate-3 group-hover:scale-110`}
                  >
                    <Icon className="h-10 w-10" />
                    {/* Floating particles effect */}
                    <div className="absolute -inset-4 rounded-2xl bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 blur-xl transition-all duration-500 group-hover:animate-pulse group-hover:opacity-60" />
                  </div>
                  {/* Background glow */}
                  <div
                    className={`absolute inset-0 h-20 w-20 rounded-2xl bg-gradient-to-br ${step.color} opacity-20 blur-xl transition-all duration-500 group-hover:scale-125 group-hover:opacity-40`}
                  />
                </div>

                <div className="relative z-10 text-center">
                  <h3 className="mb-3 text-xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-gray-800 dark:text-white dark:group-hover:text-gray-100">
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-600 transition-colors duration-300 group-hover:text-gray-700 dark:text-gray-300 dark:group-hover:text-gray-200">
                    {step.description}
                  </p>
                </div>

                {/* Bottom accent line */}
                <div
                  className={`absolute bottom-0 left-1/2 h-1 w-0 rounded-full bg-gradient-to-r ${step.color} transition-all duration-500 group-hover:w-16 group-hover:-translate-x-1/2`}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            Ready to transform your financial operations?
          </p>
          <div className="mx-auto h-px w-32 bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
        </motion.div>
      </div>
    </section>
  );
}

function Fintechcontact() {
  const navigate = useNavigate();

  return (
    <section className="relative mx-auto max-w-6xl overflow-hidden px-6 py-32">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(252,211,77,0.12),transparent_60%)]" />
        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(252,211,77,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(252,211,77,0.05)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      {/* Content */}
      <div className="text-left">
        <h2 className="relative z-10 text-4xl font-extrabold text-gray-900 sm:text-5xl dark:text-gray-100">
          We Know How Complex
          <span className="mt-2 block bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
            Fintech Systems Work
          </span>
        </h2>
        <p className="relative z-10 mt-6 max-w-2xl text-lg text-gray-700 dark:text-gray-300">
          Our expert engineers build scalable, reliable, and cutting-edge solutions tailored for the
          financial sector. Let's accelerate your growth, optimize operations, and bring your ideas
          to life.
        </p>

        {/* Button */}
        <button
          onClick={() => navigate('/contacts')}
          className="relative z-10 mt-8 flex items-center gap-3 rounded-full bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 px-8 py-4 text-sm font-semibold text-white shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl"
        >
          Contact Our Team
          <ArrowRightIcon className="h-5 w-5" />
        </button>
      </div>
    </section>
  );
}
export default function Fintech() {
  return (
    <div className="mx-auto sm:px-6 lg:px-8">
      <FintechHero />
      <HowItWorks />
      <Fintechcontact />
    </div>
  );
}
